import {
  Box3,
  Frustum,
  Matrix4,
  Vector3,
  WebGPUCoordinateSystem,
} from 'rewild-common';
import { Renderer } from '../../Renderer';
import { PerspectiveCamera } from '../../core/PerspectiveCamera';
import { IRenderGroup } from '../../../types/IRenderGroup';
import {
  IScatterInstanceGroup,
  IVisualComponent,
} from '../../../types/interfaces';
import shader from '../../shaders/shadow-depth.wgsl';
import instancedShader from '../../shaders/shadow-depth-instanced.wgsl';
import impostorShader from '../../shaders/shadow-depth-impostor.wgsl';
import { ScatterImpostorPass } from '../../materials/ScatterImpostorPass';
import { ScatterInstancedPass } from '../../materials/ScatterInstancedPass';
import { writeScatterWindParams } from '../terrain/ScatterLayers';
import { ShadowDebugRenderer } from './ShadowDebugRenderer';
import {
  isScatterImpostorPass,
  isScatterInstanceGroup,
} from '../../typeGuards';
import { shadowConfig } from './ShadowQuality';

export const NUM_CASCADES = 3;

// Cascade edge the shader's per-cascade normal offsets are tuned at, in texels.
// See normalOffsetScale.
const NORMAL_OFFSET_REFERENCE_CASCADE_SIZE = 1024;

// Practical-split blend factor λ: 0 = uniform spacing, 1 = logarithmic spacing.
// Higher values pull cascade 0 much tighter (better near-shadow texel density).
const CSM_LAMBDA = 0.7;

// How far each cascade's ortho box is pushed *toward the light*, beyond the
// receivers it covers, so that casters standing outside the slice are still
// drawn into its depth map.
//
// A cascade's box is fitted to the ground it shades, but the thing casting onto
// that ground is up-light of it and usually outside the slice entirely. Anything
// not inside the box is never rendered, so the receivers find no occluder and
// come out fully lit. This bit the near cascade alone: cascade 0 is pulled tight
// by CSM_LAMBDA, so its box spans only tens of metres, while cascades 1 and 2 are
// large enough to swallow the caster by accident. The near ground rendered
// unshadowed under a dune that plainly shadows everything behind it, and the
// unshadowed patch tracked cascade 0's footprint — so it slid around as the
// camera turned, while nothing in the world had moved.
//
// Sized from the shallowest sun that still casts: the shader fades shadows out
// below ~17° elevation (see _sunElevationFade), and at that angle a 100m dune
// throws its shadow a bit over 300m, putting the caster ~300 units up-light of
// the receiver. 500 clears that with room spare. Raise it if shadows go missing
// on the near ground under tall casters at low sun; the only cost is depth range
// (harmless at depth32float) and slightly coarser effective bias.
const CASTER_EXTENSION_TOWARD_LIGHT = 500;

const FAR_CASCADE = NUM_CASCADES - 1;

// Metres between the positions the light view may pivot on. Moving to the next
// re-aliases shadow edges once, so it is coarse, but small enough to keep the
// pivot near the camera. See _computeCascadeLightVPs.
const LIGHT_ANCHOR_GRID = 128;

// Float index of windParams.x, the sway amplitude, in an instanced caster's
// uniforms.
const WIND_AMPLITUDE_INDEX = 44;

// Frames between re-renders of the far cascade. Between them it keeps its depth
// and the light matrix it was drawn with, so the shader still samples it
// consistently.
const FAR_CASCADE_INTERVAL = 2;

// Writes depth 1.0 (fully lit) over the current viewport: a fullscreen
// triangle at the far plane. Clears one atlas quadrant inside a pass that
// loads the rest.
const QUADRANT_CLEAR_SHADER = /* wgsl */ `
@vertex
fn vs(@builtin(vertex_index) index : u32) -> @builtin(position) vec4f {
  let uv = vec2f(f32((index << 1u) & 2u), f32(index & 2u));
  return vec4f(uv * 2.0 - 1.0, 1.0, 1.0);
}
`;

// Atlas quadrant [x, y] for each cascade, in units of cascadeSize.
// Layout: cascade 0 = top-left, cascade 1 = top-right, cascade 2 = bottom-left.
// The bottom-right quadrant is reserved for the spot light shadow map.
const CASCADE_QUADRANT_X = [0, 1, 0];
const CASCADE_QUADRANT_Y = [0, 0, 1];

// NDC corners used to reconstruct the view frustum in world space.
// Camera projection uses OpenGL convention: near → NDC Z = -1, far → NDC Z = +1.
const NDC_NEAR_Z = -1;
const NDC_FAR_Z = 1;
const NDC_CORNERS: [number, number, number][] = [
  [-1, -1, NDC_NEAR_Z],
  [1, -1, NDC_NEAR_Z],
  [1, 1, NDC_NEAR_Z],
  [-1, 1, NDC_NEAR_Z],
  [-1, -1, NDC_FAR_Z],
  [1, -1, NDC_FAR_Z],
  [1, 1, NDC_FAR_Z],
  [-1, 1, NDC_FAR_Z],
];

// shadowMVP + nodeMatrix + viewer + range + wind + windParams + windOrigin,
// matching Uniforms in shadow-depth-instanced.wgsl.
const INSTANCED_UNIFORM_BYTES = 64 * 2 + 80;
// shadowMVP + viewer + range + lightDir, matching Uniforms in
// shadow-depth-impostor.wgsl.
const IMPOSTOR_UNIFORM_BYTES = 64 + 16 * 3;

// Which pipeline a caster draws through. A group's bind group entries differ
// between the three, so the kind decides both the write and the draw.
type ShadowCasterKind = 'mesh' | 'instanced' | 'impostor';

interface MeshShadowUniforms {
  buffers: [GPUBuffer, GPUBuffer, GPUBuffer];
  bindGroups: [GPUBindGroup, GPUBindGroup, GPUBindGroup];
  kind: ShadowCasterKind;
  // Whether an instanced caster is a cutout the shadow shader has to test.
  cutout: boolean;
  // Whether an instanced caster sways: it binds COLOR_0 and draws through the
  // wind pipeline, so its shadow bends with it.
  wind: boolean;
}

/** Least sun a shadowed fragment keeps, 0..1. */
export interface ShadowFloors {
  ground: number;
  /** A canopy lets light through and between its leaves, so it keeps more. */
  foliage: number;
}

export class DirectionalShadowRenderer {
  shadowDepthTexture: GPUTexture;
  debugRenderer: ShadowDebugRenderer;
  /** True while shadow cascade debug tint is active — read by EnvironmentUniforms to set debugMode uniform. */
  debugMode: boolean = false;
  /** Per-cascade light VP matrices — updated each frame, read by EnvironmentUniforms.prepare(). */
  lightVPs: [Matrix4, Matrix4, Matrix4];
  /**
   * View-space depth at which each cascade ends, plus sun elevation in [3].
   *   [0] = end of cascade 0  (cascadeSplits.x in shader)
   *   [1] = end of cascade 1  (cascadeSplits.y in shader)
   *   [2] = shadowFar (cascadeSplits.z in shader)
   *   [3] = normalized sun direction Y (cascadeSplits.w) — used by shader to fade shadows near horizon
   */
  cascadeSplitDistances: Float32Array;
  /**
   * Edge of one cascade's atlas quadrant, in texels; the atlas is twice this.
   * Read by the spot pass to place its own quadrant. Set from the shadows
   * quality tier.
   */
  cascadeSize: number = 0;
  /** View-space distance past which nothing receives a directional shadow. */
  shadowFar: number = 0;
  /**
   * Multiplier on the shader's per-cascade normal-offset bias — read by
   * EnvironmentUniforms.prepare(). The offsets are tuned in metres at a
   * NORMAL_OFFSET_REFERENCE_CASCADE_SIZE cascade and only need to clear a
   * texel or so, so they shrink as the cascade grows and its texels get finer.
   */
  normalOffsetScale: number = 1;
  /** Shadow floors while SSAO is off. */
  readonly baseShadowFloors: ShadowFloors = { ground: 0.25, foliage: 0.475 };
  /**
   * Shadow floors while SSAO is applied. Lower, because the floor is sun the
   * AO cannot darken, and at the base values it washes contact shadows out.
   */
  readonly aoShadowFloors: ShadowFloors = { ground: 0.2, foliage: 0.6 };

  private pipeline: GPURenderPipeline;
  private builtQualityRevision: number = -1;
  // The atlas the previous frame sampled. EnvironmentUniforms rebinds one frame
  // after the swap, so the old texture must outlive the frame that swapped it.
  private retiredAtlas: GPUTexture | null = null;
  private instancedPipeline: GPURenderPipeline;
  private instancedWindPipeline: GPURenderPipeline;
  private impostorPipeline: GPURenderPipeline;
  private meshUniforms: Map<IVisualComponent, MeshShadowUniforms>;
  // Scratch set reused by the per-frame stale-entry sweep (no per-frame allocation).
  private _liveMeshes = new Set<IVisualComponent>();

  // Per cascade, the light frustum casters are culled against.
  private _cascadeFrusta: [Frustum, Frustum, Frustum];
  // Bit c set: the caster is drawn into cascade c this frame.
  private _cascadeMasks = new Map<IVisualComponent, number>();
  private _casterBox = new Box3();
  // Whether a cascade's quadrant holds a render it can be sampled from.
  private _cascadeValid = [false, false, false];
  private _farCascadeVP = new Matrix4();
  private _frameIndex = 0;
  private _quadrantClearPipeline: GPURenderPipeline;

  // Pre-allocated per-frame math — never allocated inside render().
  private _lightViewWorld: Matrix4;
  private _lightView: Matrix4;
  private _lightProj: Matrix4;
  private _shadowMVP: Matrix4;
  private _frustumCorners: Vector3[]; // 8 full-frustum corners (near + far planes)
  private _cascadeCorners: Vector3[]; // 8 sub-frustum corners for the current cascade
  private _origin: Vector3;
  private _frustumCenter: Vector3;
  private _lightDir: Vector3;
  private _lightUp: Vector3;
  private _matData: Float32Array;
  private _instancedData: Float32Array;
  private _impostorData: Float32Array;
  private _chunkInverse: Matrix4;
  private _viewerLocal: Vector3;

  constructor() {
    this.debugRenderer = new ShadowDebugRenderer();
    this.lightVPs = [new Matrix4(), new Matrix4(), new Matrix4()];
    this._cascadeFrusta = [new Frustum(), new Frustum(), new Frustum()];
    this.cascadeSplitDistances = new Float32Array(4);
    this.meshUniforms = new Map();
    this._lightViewWorld = new Matrix4();
    this._lightView = new Matrix4();
    this._lightProj = new Matrix4();
    this._shadowMVP = new Matrix4();
    this._frustumCorners = Array.from({ length: 8 }, () => new Vector3());
    this._cascadeCorners = Array.from({ length: 8 }, () => new Vector3());
    this._origin = new Vector3();
    this._frustumCenter = new Vector3();
    this._lightDir = new Vector3();
    this._lightUp = new Vector3();
    this._matData = new Float32Array(16);
    this._instancedData = new Float32Array(INSTANCED_UNIFORM_BYTES / 4);
    this._impostorData = new Float32Array(IMPOSTOR_UNIFORM_BYTES / 4);
    this._chunkInverse = new Matrix4();
    this._viewerLocal = new Vector3();
  }

  /** The floors in force, read by EnvironmentUniforms.prepare(). */
  shadowFloors(aoApplied: boolean): ShadowFloors {
    return aoApplied ? this.aoShadowFloors : this.baseShadowFloors;
  }

  init(renderer: Renderer): void {
    const { device } = renderer;

    const module = device.createShaderModule({
      label: 'shadow depth shader',
      code: shader,
    });

    this.pipeline = device.createRenderPipeline({
      label: 'directional shadow pipeline',
      layout: 'auto',
      vertex: {
        entryPoint: 'vs',
        module,
        buffers: [
          {
            arrayStride: 4 * 3,
            attributes: [{ shaderLocation: 0, offset: 0, format: 'float32x3' }],
          },
        ],
      },
      // Depth-only — no fragment stage needed.
      primitive: {
        topology: 'triangle-list',
        cullMode: 'none',
      },
      depthStencil: {
        depthWriteEnabled: true,
        depthCompare: 'less',
        format: 'depth32float',
        depthBiasSlopeScale: 2.0,
      },
    });

    const instancedModule = device.createShaderModule({
      label: 'instanced shadow depth shader',
      code: instancedShader,
    });

    // Same depth state as the per-mesh pipeline, bias included: a caster
    // stored at a different depth offset from the terrain it stands on would
    // acne against it at the contact. The fragment stage has no targets; it
    // only cuts leaf cards out of the depth they would otherwise write whole.
    const instancedBuffers: GPUVertexBufferLayout[] = [
      {
        arrayStride: 4 * 3,
        attributes: [{ shaderLocation: 0, offset: 0, format: 'float32x3' }],
      },
      {
        arrayStride: 4 * 2,
        attributes: [{ shaderLocation: 1, offset: 0, format: 'float32x2' }],
      },
    ];
    const instancedPipeline = (
      label: string,
      entryPoint: string,
      buffers: GPUVertexBufferLayout[]
    ): GPURenderPipeline =>
      device.createRenderPipeline({
        label,
        layout: 'auto',
        vertex: { entryPoint, module: instancedModule, buffers },
        fragment: { entryPoint: 'fs', module: instancedModule, targets: [] },
        primitive: {
          topology: 'triangle-list',
          cullMode: 'none',
        },
        depthStencil: {
          depthWriteEnabled: true,
          depthCompare: 'less',
          format: 'depth32float',
          depthBiasSlopeScale: 2.0,
        },
      });

    this.instancedPipeline = instancedPipeline(
      'directional shadow instanced pipeline',
      'vs',
      instancedBuffers
    );
    // The wind variant also binds COLOR_0, the bend weights the scene pass
    // displaces by.
    this.instancedWindPipeline = instancedPipeline(
      'directional shadow instanced wind pipeline',
      'vsWind',
      [
        ...instancedBuffers,
        {
          arrayStride: 4 * 4,
          attributes: [{ shaderLocation: 2, offset: 0, format: 'float32x4' }],
        },
      ]
    );

    const impostorModule = device.createShaderModule({
      label: 'impostor shadow depth shader',
      code: impostorShader,
    });

    // A fragment stage with no targets: the billboard is a cutout, so the
    // atlas alpha has to decide which texels write depth.
    this.impostorPipeline = device.createRenderPipeline({
      label: 'directional shadow impostor pipeline',
      layout: 'auto',
      vertex: {
        entryPoint: 'vs',
        module: impostorModule,
        buffers: [
          {
            arrayStride: 4 * 3,
            attributes: [{ shaderLocation: 0, offset: 0, format: 'float32x3' }],
          },
          {
            arrayStride: 4 * 2,
            attributes: [{ shaderLocation: 1, offset: 0, format: 'float32x2' }],
          },
        ],
      },
      fragment: { entryPoint: 'fs', module: impostorModule, targets: [] },
      primitive: {
        topology: 'triangle-list',
        cullMode: 'none',
      },
      depthStencil: {
        depthWriteEnabled: true,
        depthCompare: 'less',
        format: 'depth32float',
        depthBiasSlopeScale: 2.0,
      },
    });

    const clearModule = device.createShaderModule({
      label: 'shadow quadrant clear shader',
      code: QUADRANT_CLEAR_SHADER,
    });
    this._quadrantClearPipeline = device.createRenderPipeline({
      label: 'shadow quadrant clear pipeline',
      layout: 'auto',
      vertex: { entryPoint: 'vs', module: clearModule },
      primitive: { topology: 'triangle-list' },
      depthStencil: {
        depthWriteEnabled: true,
        depthCompare: 'always',
        format: 'depth32float',
      },
    });

    this.debugRenderer.init(renderer);
    this._buildAtlas(renderer);
  }

  /**
   * Sizes the atlas from the shadows quality tier: four cascadeSize quadrants,
   * cascades 0/1/2 top-left / top-right / bottom-left, bottom-right reserved
   * for the spot light. Called again whenever the tier moves; the pipelines do
   * not depend on the size, so only the texture is replaced.
   */
  private _buildAtlas(renderer: Renderer): void {
    const { device, quality } = renderer;
    const tier = shadowConfig(quality.aspect('shadows'));
    this.builtQualityRevision = quality.revision;

    this.shadowFar = tier.shadowFar;
    if (this.cascadeSize === tier.cascadeSize) return;

    this.cascadeSize = tier.cascadeSize;
    this.normalOffsetScale =
      NORMAL_OFFSET_REFERENCE_CASCADE_SIZE / tier.cascadeSize;

    this.retiredAtlas?.destroy();
    this.retiredAtlas = this.shadowDepthTexture ?? null;

    this._cascadeValid.fill(false);
    const atlasSize = tier.cascadeSize * 2;
    this.shadowDepthTexture = device.createTexture({
      label: 'directional shadow depth',
      size: [atlasSize, atlasSize, 1],
      format: 'depth32float',
      usage:
        GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    this.debugRenderer.bindAtlas(device, this.shadowDepthTexture);
  }

  render(
    encoder: GPUCommandEncoder,
    renderList: IRenderGroup[],
    camera: PerspectiveCamera,
    renderer: Renderer
  ): void {
    const sun = renderer.sky?.skyRenderer?.keyLight;
    if (!sun) return;

    // When the sun is at or below the horizon its light comes from underground, which produces
    // broken shadows on terrain (backface-culled polygons leave the map in a garbage state).
    // In that case we still open the pass so the atlas clears to 1.0 (= fully lit / no shadow),
    // but we skip the cascade computation and all draw calls.
    const sunAboveHorizon = sun.transform.position.y > 0;

    const { device } = renderer;

    // The frame that swapped the atlas has been submitted by now, so the old
    // one has no reader left.
    if (this.retiredAtlas) {
      this.retiredAtlas.destroy();
      this.retiredAtlas = null;
    }
    if (renderer.quality.hasChangedSince(this.builtQualityRevision)) {
      this._buildAtlas(renderer);
    }

    // Meshes come and go (actor removal, chunk eviction, terrain sculpting
    // replaces a chunk's mesh every stamp) — drop uniform entries for meshes
    // no longer in the caster list, or the map pins every dead mesh's whole
    // geometry forever and the tab runs out of memory.
    this._sweepStaleMeshUniforms(renderList);

    this._frameIndex++;
    const renderFar =
      !sunAboveHorizon ||
      !this._cascadeValid[FAR_CASCADE] ||
      this._frameIndex % FAR_CASCADE_INTERVAL === 0;
    const drawMask =
      (1 << NUM_CASCADES) - 1 - (renderFar ? 0 : 1 << FAR_CASCADE);

    if (sunAboveHorizon) {
      this._computeCascadeLightVPs(camera, sun.transform.position);

      // A held far cascade keeps the matrix it was drawn with, so the shader
      // samples the depth it holds with the projection that produced it.
      if (renderFar) this._farCascadeVP.copy(this.lightVPs[FAR_CASCADE]);
      else this.lightVPs[FAR_CASCADE].copy(this._farCascadeVP);

      for (let c = 0; c < NUM_CASCADES; c++)
        this._cascadeFrusta[c].setFromProjectionMatrix(
          this.lightVPs[c],
          WebGPUCoordinateSystem
        );
      this._cascadeMasks.clear();

      // Ensure per-mesh uniform buffers exist (geometry must already be built).
      for (const item of renderList) {
        if (item.geometry.requiresBuild) continue;
        for (const mesh of item.meshes) {
          this._ensureMeshUniforms(renderer, mesh);
        }
      }

      // Write all 3 cascade shadow MVPs for every mesh before opening the render pass.
      // Buffer writes must happen outside the pass so each cascade draw sees its own matrix.
      for (const item of renderList) {
        if (item.geometry.requiresBuild) continue;
        for (const mesh of item.meshes) {
          const uniforms = this.meshUniforms.get(mesh);
          if (!uniforms) continue;

          const mask = this._cascadeMask(mesh) & drawMask;
          this._cascadeMasks.set(mesh, mask);
          if (mask === 0) continue;

          if (uniforms.kind !== 'mesh') {
            this._writeInstancedUniforms(
              renderer,
              uniforms,
              mesh as IScatterInstanceGroup,
              camera
            );
            continue;
          }

          for (let c = 0; c < NUM_CASCADES; c++) {
            if ((mask & (1 << c)) === 0) continue;
            this._shadowMVP.multiplyMatrices(
              this.lightVPs[c],
              mesh.transform.matrixWorld
            );
            this._matData.set(this._shadowMVP.elements);
            device.queue.writeBuffer(
              uniforms.buffers[c],
              0,
              this._matData.buffer
            );
          }
        }
      }
    }

    const depthView = this.shadowDepthTexture.createView();
    const pass = encoder.beginRenderPass({
      label: 'directional shadow pass (CSM)',
      colorAttachments: [],
      depthStencilAttachment: {
        view: depthView,
        depthClearValue: 1.0,
        depthLoadOp: renderFar ? 'clear' : 'load',
        depthStoreOp: 'store',
      },
      timestampWrites: renderer.sceneGpuTimer.writes('shadow'),
    });

    // Loading keeps the held far cascade. Every other quadrant, the spot
    // light's included, still has to start from fully lit.
    if (!renderFar) {
      const size = this.cascadeSize;
      pass.setPipeline(this._quadrantClearPipeline);
      for (let c = 0; c < NUM_CASCADES; c++) {
        if (c === FAR_CASCADE) continue;
        pass.setViewport(
          CASCADE_QUADRANT_X[c] * size,
          CASCADE_QUADRANT_Y[c] * size,
          size,
          size,
          0,
          1
        );
        pass.draw(3);
      }
      // The spot light's quadrant, bottom-right.
      pass.setViewport(size, size, size, size, 0, 1);
      pass.draw(3);
    }

    if (sunAboveHorizon) {
      // Render all 3 cascades in a single pass — setViewport routes each into its atlas quadrant.
      const size = this.cascadeSize;
      const cameraWorld = camera.camera.transform.matrixWorld.elements;
      for (let c = 0; c < NUM_CASCADES; c++) {
        if ((drawMask & (1 << c)) === 0) continue;
        pass.setViewport(
          CASCADE_QUADRANT_X[c] * size,
          CASCADE_QUADRANT_Y[c] * size,
          size,
          size,
          0,
          1
        );

        // organizeVisuals groups by geometry and pass, so a group is entirely
        // of one kind; the pipeline only changes where the render list crosses
        // from one kind to the next.
        let currentPipeline: GPURenderPipeline | null = null;

        for (const item of renderList) {
          if (item.geometry.requiresBuild) continue;

          const geo = item.geometry;
          pass.setVertexBuffer(0, geo.vertexBuffer);
          pass.setIndexBuffer(geo.indexBuffer, 'uint32');
          const numIndices = geo.indices!.length;

          for (const mesh of item.meshes) {
            const uniforms = this.meshUniforms.get(mesh);
            if (!uniforms) continue;
            if (((this._cascadeMasks.get(mesh) ?? 0) & (1 << c)) === 0)
              continue;

            const wanted =
              uniforms.kind === 'impostor'
                ? this.impostorPipeline
                : uniforms.kind === 'instanced'
                ? uniforms.wind
                  ? this.instancedWindPipeline
                  : this.instancedPipeline
                : this.pipeline;
            if (wanted !== currentPipeline) {
              pass.setPipeline(wanted);
              currentPipeline = wanted;
            }

            if (uniforms.kind === 'mesh') {
              pass.setBindGroup(0, uniforms.bindGroups[c]);
              pass.drawIndexed(numIndices, 1);
              continue;
            }

            const group = mesh as IScatterInstanceGroup;
            // Each cascade selects the cells its own light frustum reaches.
            // Chunk transforms only translate.
            const world = group.transform.matrixWorld.elements;
            this._viewerLocal.set(
              cameraWorld[12] - world[12],
              cameraWorld[13] - world[13],
              cameraWorld[14] - world[14]
            );
            group.selectInstances(
              this._viewerLocal,
              this._cascadeFrusta[c],
              true
            );
            if (group.rangeCount === 0) continue;
            // Both scatter casters carry a uv, for their cutouts.
            pass.setVertexBuffer(1, geo.uvBuffer);
            if (uniforms.wind) pass.setVertexBuffer(2, geo.colorBuffer);
            pass.setBindGroup(0, uniforms.bindGroups[c]);
            for (let r = 0; r < group.rangeCount; r++)
              pass.drawIndexed(
                numIndices,
                group.rangeCounts[r],
                0,
                0,
                group.rangeStarts[r]
              );
          }
        }
      }
    }

    pass.end();

    for (let c = 0; c < NUM_CASCADES; c++)
      if (!sunAboveHorizon) this._cascadeValid[c] = false;
      else if (drawMask & (1 << c)) this._cascadeValid[c] = true;
  }

  // Which cascades a caster's world bounds reach, as bits. A caster with no
  // bounds goes into every cascade. Each cascade's frustum is the whole depth
  // range it keeps, already pushed toward the light by
  // CASTER_EXTENSION_TOWARD_LIGHT, so this drops only geometry the GPU would
  // clip.
  private _cascadeMask(mesh: IVisualComponent): number {
    const all = (1 << NUM_CASCADES) - 1;

    const local = mesh.localBounds ?? mesh.geometry.boundingBox;
    if (!local) return all;

    const box = this._casterBox
      .copy(local)
      .applyMatrix4(mesh.transform.matrixWorld);
    let mask = 0;
    for (let c = 0; c < NUM_CASCADES; c++)
      if (this._cascadeFrusta[c].intersectsBox(box)) mask |= 1 << c;
    return mask;
  }

  dispose(): void {
    this.retiredAtlas?.destroy();
    this.retiredAtlas = null;
    this.shadowDepthTexture?.destroy();
    for (const { buffers } of this.meshUniforms.values()) {
      for (const buf of buffers) buf.destroy();
    }
    this.meshUniforms.clear();
  }

  // The shadow render list is collected from the full scene (not camera-
  // culled), so membership only changes when a mesh is actually added to or
  // removed from the scene — entries never thrash with camera movement.
  private _sweepStaleMeshUniforms(renderList: IRenderGroup[]): void {
    const live = this._liveMeshes;
    live.clear();
    for (const item of renderList) {
      for (const mesh of item.meshes) live.add(mesh);
    }
    for (const [mesh, uniforms] of this.meshUniforms) {
      if (live.has(mesh)) continue;
      for (const buf of uniforms.buffers) buf.destroy();
      this.meshUniforms.delete(mesh);
    }
    live.clear();
  }

  private _ensureMeshUniforms(
    renderer: Renderer,
    mesh: IVisualComponent
  ): void {
    if (this.meshUniforms.has(mesh)) return;

    const { device } = renderer;

    if (isScatterInstanceGroup(mesh)) {
      // The scene pass would upload this on its first draw, but the shadow pass
      // runs first in the frame, so it is as likely to be the one that does.
      const instanceBuffer = mesh.instanceStorageBuffer(renderer);
      // An empty group has no buffer to bind; leaving it out of the map also
      // keeps it out of the draw loop.
      if (!instanceBuffer) return;

      const impostor = isScatterImpostorPass(mesh.material)
        ? (mesh.material as ScatterImpostorPass)
        : null;
      const kind: ShadowCasterKind = impostor ? 'impostor' : 'instanced';
      const pass = impostor ? null : (mesh.material as ScatterInstancedPass);
      const wind = pass?.wind != null;
      // The scene pass would bind the material's defaults at its build, which
      // may not have run yet for a group the shadow pass meets first.
      const material = pass ? pass.material : null;

      const buffers = [0, 1, 2].map(() =>
        device.createBuffer({
          label: `shadow ${kind} uniforms`,
          size: impostor ? IMPOSTOR_UNIFORM_BYTES : INSTANCED_UNIFORM_BYTES,
          usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        })
      ) as [GPUBuffer, GPUBuffer, GPUBuffer];

      const bindGroups = buffers.map((buffer) =>
        device.createBindGroup({
          label: `shadow ${kind} bind group`,
          layout: (impostor
            ? this.impostorPipeline
            : wind
            ? this.instancedWindPipeline
            : this.instancedPipeline
          ).getBindGroupLayout(0),
          entries: [
            { binding: 0, resource: { buffer } },
            { binding: 1, resource: { buffer: instanceBuffer } },
            ...(impostor
              ? [
                  { binding: 2, resource: impostor.atlas.sampler },
                  { binding: 3, resource: impostor.atlas.albedo.createView() },
                  { binding: 4, resource: { buffer: impostor.atlas.params } },
                ]
              : [
                  {
                    binding: 2,
                    resource:
                      material!.sampler ||
                      renderer.samplerManager.get('linear'),
                  },
                  {
                    binding: 3,
                    resource: (
                      material!.baseColorTexture ||
                      renderer.textureManager.get('white-1x1').gpuTexture
                    ).createView(),
                  },
                ]),
          ],
        })
      ) as [GPUBindGroup, GPUBindGroup, GPUBindGroup];

      const cutout = pass?.alphaMode === 'MASK';
      this.meshUniforms.set(mesh, {
        buffers,
        bindGroups,
        kind,
        cutout,
        wind,
      });
      return;
    }

    const buffers = [0, 1, 2].map(() =>
      device.createBuffer({
        label: 'shadow mesh MVP',
        size: 64,
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      })
    ) as [GPUBuffer, GPUBuffer, GPUBuffer];

    const bindGroups = buffers.map((buffer) =>
      device.createBindGroup({
        label: 'shadow mesh bind group',
        layout: this.pipeline.getBindGroupLayout(0),
        entries: [{ binding: 0, resource: { buffer } }],
      })
    ) as [GPUBindGroup, GPUBindGroup, GPUBindGroup];

    this.meshUniforms.set(mesh, {
      buffers,
      bindGroups,
      kind: 'mesh',
      cutout: false,
      wind: false,
    });
  }

  /**
   * One instanced or impostor group's three cascade uniforms.
   *
   * Only the MVP differs between cascades, but each buffer is written whole —
   * one small write beats tracking which half went stale.
   *
   * The viewer is put in chunk-local space so the shader's per-instance cull
   * matches the scene pass's without carrying a second matrix. The chunk's
   * model-view cannot stand in for it: shadow casters come from the whole
   * scene, so a chunk outside the camera frustum still holds the model-view
   * from whenever it was last visible.
   */
  private _writeInstancedUniforms(
    renderer: Renderer,
    uniforms: MeshShadowUniforms,
    group: IScatterInstanceGroup,
    camera: PerspectiveCamera
  ): void {
    const { device } = renderer;
    const world = group.transform.matrixWorld;
    const cameraWorld = camera.camera.transform.matrixWorld.elements;

    this._chunkInverse.copy(world).invert();
    this._viewerLocal
      .set(cameraWorld[12], cameraWorld[13], cameraWorld[14])
      .applyMatrix4(this._chunkInverse);

    // The same viewer decides which cells the band can reach, so the caster
    // set is the drawn set down to the run.
    group.selectInstances(this._viewerLocal, null);

    const viewer = this._viewerLocal;
    let data: Float32Array;
    if (uniforms.kind === 'impostor') {
      data = this._impostorData;
      data[16] = viewer.x;
      data[17] = viewer.y;
      data[18] = viewer.z;
      data[20] = group.nearDistance;
      data[21] = group.cullDistance;
      // Chunk transforms only translate, so the light's direction is the same
      // in chunk-local space as in the world.
      data[24] = this._lightDir.x;
      data[25] = this._lightDir.y;
      data[26] = this._lightDir.z;
    } else {
      data = this._instancedData;
      data.set(group.nodeMatrix, 16);
      data[32] = viewer.x;
      data[33] = viewer.y;
      data[34] = viewer.z;
      data[36] = group.nearDistance;
      data[37] = group.cullDistance;
      data[38] = uniforms.cutout ? 1 : 0;
      // The same wind the scene pass reads this frame, so the shadow of a
      // leaf lands where the leaf is.
      const wind = renderer.sky.skyRenderer.wind;
      data.set(wind.vec, 40);
      writeScatterWindParams(
        (group.material as ScatterInstancedPass).wind,
        world.elements[12],
        world.elements[14],
        wind.gustDrift,
        data,
        44
      );
    }

    // The far cascade's texels are wider than a branch sways, so a swaying
    // caster only flickers whole texels on and off there; it casts at rest.
    const amplitude = data[WIND_AMPLITUDE_INDEX];
    for (let c = 0; c < NUM_CASCADES; c++) {
      this._shadowMVP.multiplyMatrices(this.lightVPs[c], world);
      data.set(this._shadowMVP.elements, 0);
      if (uniforms.wind)
        data[WIND_AMPLITUDE_INDEX] = c === FAR_CASCADE ? 0 : amplitude;
      device.queue.writeBuffer(uniforms.buffers[c], 0, data.buffer);
    }
  }

  private _computeCascadeLightVPs(
    camera: PerspectiveCamera,
    sunPosition: Vector3
  ): void {
    const camCamera = camera.camera;
    const projInv = camCamera.projectionMatrixInverse;
    const camWorld = camCamera.transform.matrixWorld;
    const camNear = camera.near;
    const camFar = camera.far;

    // --- Full-frustum corners in world space ---
    for (let i = 0; i < 8; i++) {
      const [nx, ny, nz] = NDC_CORNERS[i];
      this._frustumCorners[i].set(nx, ny, nz).unproject(projInv, camWorld);
    }

    // --- Light orientation (shared across all cascades) ---
    const sunLen = Math.sqrt(
      sunPosition.x * sunPosition.x +
        sunPosition.y * sunPosition.y +
        sunPosition.z * sunPosition.z
    );
    if (sunLen < 0.0001) return;
    this._lightDir.set(
      sunPosition.x / sunLen,
      sunPosition.y / sunLen,
      sunPosition.z / sunLen
    );

    if (Math.abs(this._lightDir.y) > 0.99) {
      this._lightUp.set(1, 0, 0);
    } else {
      this._lightUp.set(0, 1, 0);
    }

    // Light view shared by every cascade, pivoting on an anchor that only moves
    // in LIGHT_ANCHOR_GRID steps. Between steps a point's light-space position
    // is fixed, so snapping it to texels locks the grid to the world. A view
    // that followed the cascade centre would put that centre at (0, 0) every
    // frame and leave the snap nothing to bite on.
    //
    // The anchor is kept near the camera because a turning sun swings the grid
    // about it: a point d metres away slides d × the turn through the texels.
    // Pivoting on the world origin made that d the player's distance from it,
    // enough at a few hundred metres to re-alias every shadow edge each frame.
    const camPos = camWorld.elements;
    this._lightViewWorld.lookAt(this._lightDir, this._origin, this._lightUp);
    this._lightViewWorld.setPosition(
      Math.round(camPos[12] / LIGHT_ANCHOR_GRID) * LIGHT_ANCHOR_GRID,
      Math.round(camPos[13] / LIGHT_ANCHOR_GRID) * LIGHT_ANCHOR_GRID,
      Math.round(camPos[14] / LIGHT_ANCHOR_GRID) * LIGHT_ANCHOR_GRID
    );
    this._lightView.copy(this._lightViewWorld).invert();

    // --- Practical-split cascade distances ---
    // cascadeSplitDistances[i] is the VIEW-SPACE depth where cascade i ends.
    const shadowFar = Math.min(camFar, this.shadowFar);
    for (let i = 1; i <= NUM_CASCADES; i++) {
      const cLog = camNear * Math.pow(shadowFar / camNear, i / NUM_CASCADES);
      const cUni = camNear + (shadowFar - camNear) * (i / NUM_CASCADES);
      this.cascadeSplitDistances[i - 1] =
        CSM_LAMBDA * cLog + (1 - CSM_LAMBDA) * cUni;
    }
    this.cascadeSplitDistances[3] = this._lightDir.y; // sun elevation Y for shadow fade in shader

    // --- Compute one light VP per cascade ---
    let splitNear = camNear;
    for (let c = 0; c < NUM_CASCADES; c++) {
      const splitFar = this.cascadeSplitDistances[c];

      // Lerp fractions along the full frustum edges to get the cascade sub-frustum.
      // The frustum edges are linear in world space, so lerp is exact.
      const tNear = (splitNear - camNear) / (camFar - camNear);
      const tFar = (splitFar - camNear) / (camFar - camNear);

      for (let j = 0; j < 4; j++) {
        const nj = this._frustumCorners[j]; // near-plane corner j
        const fj = this._frustumCorners[j + 4]; // far-plane corner j
        // Near face of this cascade sub-frustum
        this._cascadeCorners[j].set(
          nj.x + (fj.x - nj.x) * tNear,
          nj.y + (fj.y - nj.y) * tNear,
          nj.z + (fj.z - nj.z) * tNear
        );
        // Far face of this cascade sub-frustum
        this._cascadeCorners[j + 4].set(
          nj.x + (fj.x - nj.x) * tFar,
          nj.y + (fj.y - nj.y) * tFar,
          nj.z + (fj.z - nj.z) * tFar
        );
      }

      this._computeLightVPForCascade(c);
      splitNear = splitFar;
    }
  }

  private _computeLightVPForCascade(cascadeIndex: number): void {
    // Cascade sub-frustum center for light positioning
    this._frustumCenter.set(0, 0, 0);
    for (let i = 0; i < 8; i++) {
      this._frustumCenter.x += this._cascadeCorners[i].x;
      this._frustumCenter.y += this._cascadeCorners[i].y;
      this._frustumCenter.z += this._cascadeCorners[i].z;
    }
    this._frustumCenter.x /= 8;
    this._frustumCenter.y /= 8;
    this._frustumCenter.z /= 8;

    // Fit the ortho box to the sub-frustum's bounding SPHERE, not to a tight AABB
    // of its corners, and then snap that box to whole shadow-map texels. Both
    // halves are needed, and skipping them is what made near shadows crawl.
    //
    // A tight AABB is rotation-dependent: turning the camera on the spot sweeps
    // the 8 corners around, so the fitted bounds — and the centroid the light
    // looks at — change every frame. The cascade re-renders from a different
    // projection each time and the shadow slides across the ground while nothing
    // in the world has moved. A sphere's radius depends only on the sub-frustum's
    // *shape* (FOV, aspect, split distances), so it is invariant under rotation
    // and translation; the box then stays a fixed size and only ever moves.
    //
    // Even a fixed-size box shimmers if it can move by fractions of a texel, so
    // the centre is snapped to texel increments in light space. That locks the
    // texel grid to the world: the box advances in whole-texel steps and each
    // texel keeps covering the same ground from frame to frame.
    //
    // This is why the artefact was confined to the near field. Cascade 0 is
    // pulled tight by CSM_LAMBDA, so its texels cover tens of metres — a couple
    // of centimetres each — and sub-texel drift is a large fraction of one.
    // Cascade 2 spreads the same texels over shadowFar, where the identical
    // drift is invisible.
    //
    // The cost is resolution: a sphere circumscribes the frustum, so the box is
    // larger than a tight fit and texel density drops. That is the standard trade
    // for stable cascades — a slightly softer shadow that stays put beats a
    // sharper one that swims.
    let radius = 0;
    for (let i = 0; i < 8; i++) {
      const w = this._cascadeCorners[i];
      const dx = w.x - this._frustumCenter.x;
      const dy = w.y - this._frustumCenter.y;
      const dz = w.z - this._frustumCenter.z;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (d > radius) radius = d;
    }
    // Quantise so float noise in the corner maths cannot jitter the extent (and
    // with it the texel size) frame to frame.
    radius = Math.ceil(radius * 16) / 16;

    // Sub-frustum centre in the shared, origin-anchored light space.
    const lve = this._lightView.elements;
    const c = this._frustumCenter;
    const cx = lve[0] * c.x + lve[4] * c.y + lve[8] * c.z + lve[12];
    const cy = lve[1] * c.x + lve[5] * c.y + lve[9] * c.z + lve[13];
    const cz = lve[2] * c.x + lve[6] * c.y + lve[10] * c.z + lve[14];

    const texelSize = (2 * radius) / this.cascadeSize;
    const snappedX = Math.floor(cx / texelSize) * texelSize;
    const snappedY = Math.floor(cy / texelSize) * texelSize;

    const minX = snappedX - radius;
    const maxX = snappedX + radius;
    const minY = snappedY - radius;
    const maxY = snappedY + radius;

    // Z from the same sphere so the depth range is stable too — a near/far that
    // moved each frame would shift every stored depth and flicker the PCF
    // comparison.
    //
    // Light space looks down -Z, so anything nearer the light than the receivers
    // has a *larger* z. Growing maxZ is therefore what takes in casters; see
    // CASTER_EXTENSION_TOWARD_LIGHT. The small margin on minZ is the other end —
    // it only keeps receivers a little outside the sphere from falling through
    // the far plane and failing the shader's depth test. The view is anchored at
    // the world origin, so near may come out negative; an ortho projection only
    // needs near < far.
    const minZ = cz - radius - 50;
    const maxZ = cz + radius + CASTER_EXTENSION_TOWARD_LIGHT;

    this._makeOrthoWebGPU(minX, maxX, maxY, minY, -maxZ, -minZ);
    this.lightVPs[cascadeIndex].multiplyMatrices(
      this._lightProj,
      this._lightView
    );
  }

  /** Builds an orthographic projection matrix using WebGPU depth convention [0, 1]. */
  private _makeOrthoWebGPU(
    left: number,
    right: number,
    top: number,
    bottom: number,
    near: number,
    far: number
  ): void {
    const w = 1 / (right - left);
    const h = 1 / (top - bottom);
    const p = 1 / (far - near);
    const te = this._lightProj.elements;

    te[0] = 2 * w;
    te[4] = 0;
    te[8] = 0;
    te[12] = -(right + left) * w;
    te[1] = 0;
    te[5] = 2 * h;
    te[9] = 0;
    te[13] = -(top + bottom) * h;
    te[2] = 0;
    te[6] = 0;
    te[10] = -p;
    te[14] = -near * p;
    te[3] = 0;
    te[7] = 0;
    te[11] = 0;
    te[15] = 1;
  }
}
